import { getEnvironment } from "../../services/RuntimeConfig";
import React, { useState } from "react";
import * as Sentry from "@sentry/react";
import { Alert, Button, Card, Descriptions, Space, Typography } from "antd";

type DiagnosticAction = "message" | "error";

type DiagnosticResult = {
  type: "success" | "warning" | "error";
  title: string;
  detail: string;
  eventId?: string;
};

const FLUSH_TIMEOUT_MS = 5000;

function throwControlledDiagnosticError(): never {
  const error = new Error(
    "Error controlado para validar la conexión con Sentry (students)"
  );
  error.name = "SentryDiagnosticError";
  throw error;
}

function SentryDiagnostic() {
  const [activeAction, setActiveAction] = useState<DiagnosticAction | null>(
    null
  );
  const [result, setResult] = useState<DiagnosticResult | null>(null);

  const client = Sentry.getClient();
  const clientInitialized = Boolean(client);
  const clientEnabled = Boolean(
    client && client.getOptions().enabled !== false
  );
  const dsnConfigured = Boolean(client?.getDsn());
  const canCapture = clientEnabled && dsnConfigured;
  const environment = getEnvironment();

  const finishCapture = async (eventId: string) => {
    try {
      const flushed = await Sentry.flush(FLUSH_TIMEOUT_MS);

      setResult({
        type: flushed ? "success" : "warning",
        title: flushed
          ? "Evento listo para verificar"
          : "Sentry no confirmó el envío a tiempo",
        detail: flushed
          ? "La cola local del SDK se vació. Busca este eventId en el proyecto de Sentry."
          : `La cola local no terminó de vaciarse en ${
              FLUSH_TIMEOUT_MS / 1000
            } segundos. El eventId aún puede aparecer en Sentry.`,
        eventId,
      });
    } catch (error) {
      setResult({
        type: "error",
        title: "No se pudo completar la validación",
        detail:
          error instanceof Error
            ? error.message
            : "El SDK devolvió un error desconocido al vaciar la cola.",
        eventId,
      });
    } finally {
      setActiveAction(null);
    }
  };

  const handleCaptureFailure = (error: unknown) => {
    setResult({
      type: "error",
      title: "El SDK rechazó el evento de diagnóstico",
      detail:
        error instanceof Error
          ? error.message
          : "Ocurrió un error desconocido al crear el evento.",
    });
    setActiveAction(null);
  };

  const captureWithDiagnosticScope = (
    action: DiagnosticAction,
    capture: () => string
  ) => {
    let eventId = "";

    Sentry.withScope((scope) => {
      scope.setTag("app", "students");
      scope.setTag("diagnostic", "sentry-connectivity");
      scope.setTag("diagnostic_action", action);
      scope.setContext("diagnostic", {
        app: "students",
        diagnostic: "sentry-connectivity",
        action,
        environment,
        path: window.location.pathname,
      });
      eventId = capture();
    });

    return eventId;
  };

  const handleMessageCapture = async () => {
    if (!canCapture) {
      return;
    }

    setActiveAction("message");
    setResult(null);

    try {
      const eventId = captureWithDiagnosticScope("message", () =>
        Sentry.captureMessage(
          "Prueba manual de conexión con Sentry (students)",
          "info"
        )
      );
      await finishCapture(eventId);
    } catch (error) {
      handleCaptureFailure(error);
    }
  };

  const handleErrorCapture = async () => {
    if (!canCapture) {
      return;
    }

    setActiveAction("error");
    setResult(null);

    try {
      throwControlledDiagnosticError();
    } catch (error) {
      try {
        const eventId = captureWithDiagnosticScope("error", () =>
          Sentry.captureException(error)
        );
        await finishCapture(eventId);
      } catch (captureError) {
        handleCaptureFailure(captureError);
      }
    }
  };

  return (
    <Card className="m-5">
      <Typography.Title level={2}>Diagnóstico de Sentry</Typography.Title>
      <Typography.Paragraph>
        Esta página envía eventos intencionales para validar la conexión del
        frontend de alumnos. La ruta requiere una sesión activa y no aparece en
        la navegación.
      </Typography.Paragraph>

      <Descriptions bordered column={1} size="small" className="mb-5">
        <Descriptions.Item label="Aplicación">students</Descriptions.Item>
        <Descriptions.Item label="Entorno">{environment}</Descriptions.Item>
        <Descriptions.Item label="DSN configurado">
          {dsnConfigured ? "Sí" : "No"}
        </Descriptions.Item>
        <Descriptions.Item label="Cliente inicializado">
          {clientInitialized ? "Sí" : "No"}
        </Descriptions.Item>
        <Descriptions.Item label="Envío habilitado">
          {clientEnabled ? "Sí" : "No"}
        </Descriptions.Item>
      </Descriptions>

      {!canCapture && (
        <Alert
          className="mb-5"
          type="error"
          showIcon
          message="Sentry no está listo"
          description="Verifica el DSN y la opción enabled del cliente. El SDK no se inicializa cuando el entorno de ejecución es dev o development."
        />
      )}

      <Space wrap>
        <Button
          type="primary"
          disabled={!canCapture || activeAction !== null}
          loading={activeAction === "message"}
          onClick={handleMessageCapture}
        >
          Capturar mensaje
        </Button>
        <Button
          danger
          disabled={!canCapture || activeAction !== null}
          loading={activeAction === "error"}
          onClick={handleErrorCapture}
        >
          Capturar error controlado
        </Button>
      </Space>

      {result && (
        <Alert
          className="mt-5"
          type={result.type}
          showIcon
          message={result.title}
          description={
            <Space direction="vertical">
              <span>{result.detail}</span>
              {result.eventId && (
                <span>
                  eventId: <code>{result.eventId}</code>
                </span>
              )}
            </Space>
          }
        />
      )}
    </Card>
  );
}

export default SentryDiagnostic;
